import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Flame } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { cldOpt, cldSquare } from "../lib/img";
import { SEO, orgLd, websiteLd } from "../components/SEO";
import { FeaturedCollections } from "../components/FeaturedCollections";
import { WholesaleCards } from "../components/WholesaleCards";
import { FAQSection } from "../components/FAQSection";
import { useCart } from "../context/CartContext";

// Hardcoded Bunny CDN hero image — served as both:
//  1. The FALLBACK when the admin API hasn't loaded yet, so first paint always
//     has this image (no black flash, no Unsplash detour).
//  2. Preloaded from `public/index.html` in `<head>` so the browser starts
//     downloading it before any JS parses. Keep the URL identical in both
//     spots so the preload cache is reused by the actual <img>.
const FALLBACK_HERO_IMG = "https://vvslab.b-cdn.net/vvslab/misc/a8827c21187349d9ad7067fddecf41b7.jpg";

// Pull preloaded state baked in by prerender.js so React CSR hydrates without a
// visible flash of default hero text. Falls back to null in preview / dev.
const PRELOAD = (typeof window !== "undefined" && window.__VVS_PRELOADED_STATE__) || null;

function useCountdown(target) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (!target) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [target]);
  if (!target) return null;
  const diff = Math.max(0, new Date(target).getTime() - now);
  const d = Math.floor(diff / 86400000);
  const h = Math.floor((diff % 86400000) / 3600000);
  const m = Math.floor((diff % 3600000) / 60000);
  const s = Math.floor((diff % 60000) / 1000);
  return { d, h, m, s, done: diff === 0 };
}

function DealSection({ deal }) {
  const { addItem } = useCart();
  const cd = useCountdown(deal.ends_at);
  const products = deal.products || (deal.product ? [deal.product] : []);
  if (!deal.active || products.length === 0) return null;
  const pct = deal.discount_percent;

  return (
    <section className="border-y-2 border-black bg-black text-white" data-testid="deal-of-day">
      <div className="mx-auto max-w-7xl px-4 py-16 sm:px-8 lg:py-20">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <p className="inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.4em] text-white/70">
              <Flame size={14} />
              {deal.headline || "Limited Drop"}
            </p>
            <h1
              data-testid="deal-heading"
              className="mt-4 font-display text-5xl font-black leading-[0.95] tracking-tighter text-white sm:text-6xl lg:text-7xl"
            >
              DEAL OF THE DAY
            </h1>
            {pct > 0 && (
              <p className="mt-4 text-sm font-bold uppercase tracking-widest text-white/70">
                Save up to <span className="text-white">{Math.round(pct)}%</span> — this drop only
              </p>
            )}
          </div>
          {cd && !cd.done && (
            <div className="flex gap-2" data-testid="deal-countdown">
              {[[cd.d, "Days"], [cd.h, "Hrs"], [cd.m, "Min"], [cd.s, "Sec"]].map(([n, l]) => (
                <div key={l} className="min-w-[54px] border border-white/30 px-3 py-2 text-center">
                  <div className="font-display text-xl font-black text-white">{String(n).padStart(2, "0")}</div>
                  <div className="mt-1 text-[9px] uppercase tracking-widest text-zinc-500">{l}</div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="mt-12 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4" data-testid="deal-products-grid">
          {products.slice(0, 8).map((p) => (
            <div
              key={p.slug}
              className="group flex flex-col border-2 border-white bg-black transition-transform duration-300 hover:-translate-y-1"
              data-testid={`deal-card-${p.slug}`}
            >
              <Link to={`/${p.category_slug}/${p.slug}`} className="relative block aspect-square overflow-hidden">
                <img src={cldSquare(p.images?.[0], 800)} alt={p.name} loading="lazy" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                {pct > 0 && (
                  <span className="absolute left-2 top-2 bg-white px-2 py-1 text-[9px] font-black uppercase tracking-widest text-black">
                    -{Math.round(pct)}%
                  </span>
                )}
              </Link>
              <div className="flex flex-1 flex-col p-4">
                <Link to={`/${p.category_slug}/${p.slug}`} className="line-clamp-2 min-h-[40px] font-display text-sm font-bold text-white hover:underline">{p.name}</Link>
                <div className="mt-2 flex items-baseline gap-2">
                  <span className="text-lg font-black text-white">${p.sale_price.toFixed(2)}</span>
                  {pct > 0 && <span className="text-xs text-zinc-500 line-through">${p.price.toFixed(2)}</span>}
                </div>
                <button
                  onClick={() => {
                    addItem({ ...p, price: p.sale_price }, 1, "");
                    toast.success(`${p.name} added to cart`);
                  }}
                  data-testid={`deal-add-to-cart-${p.slug}`}
                  className="mt-auto border-2 border-white bg-white px-3 py-2 text-[11px] font-bold uppercase tracking-widest text-black transition-colors duration-200 hover:bg-transparent hover:text-white"
                >
                  Add To Cart
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  const [featured, setFeatured] = useState([]);
  const [categories, setCategories] = useState([]);
  const [posts, setPosts] = useState([]);
  const [seo, setSeo] = useState(PRELOAD?.seo || {});
  const [hero, setHero] = useState(PRELOAD?.hero || null);
  const [deal, setDeal] = useState({ active: false });
  const [social, setSocial] = useState(PRELOAD?.social || {});
  const [footer, setFooter] = useState(PRELOAD?.footer || {});
  const [branding, setBranding] = useState(PRELOAD?.branding || {});

  useEffect(() => {
    api.get("/products", { params: { featured: true, limit: 8 } }).then((r) => setFeatured(r.data));
    api.get("/categories").then((r) => setCategories(r.data));
    api.get("/blog").then((r) => setPosts(r.data.slice(0, 3)));
    api.get("/seo/home").then((r) => setSeo(r.data));
    api.get("/site/hero").then((r) => setHero(r.data));
    api.get("/deal").then((r) => setDeal(r.data));
    api.get("/site/social").then((r) => setSocial(r.data)).catch(() => {});
    api.get("/site/footer").then((r) => setFooter(r.data)).catch(() => {});
    api.get("/site/branding").then((r) => setBranding(r.data)).catch(() => {});
  }, []);

  const h = hero || {};
  // Show the Bunny CDN hero URL from the very first paint so there's no
  // blank flash while /api/site/hero resolves. Only swap to the admin-set
  // URL if it's actually a real image (skip empty strings + the placeholder
  // `example.com/hero.jpg` that mongo still ships with).
  const adminImg = (h.image || "").trim();
  const heroImg = (adminImg && !/example\.com/i.test(adminImg)) ? adminImg : FALLBACK_HERO_IMG;

  return (
    <div data-testid="home-page">
      <SEO title={seo.title || "Moissanite Hip Hop Jewelry | Iced Cuban Chains, VVS Watches & Grillz | VVS Lab"} description={seo.description || "Shop VVS moissanite hip hop jewelry that passes the diamond tester — Miami Cuban link chains, iced out moissanite watches, custom gold grillz, tennis bracelets & bubble letter pendants. Free worldwide shipping."} keywords={seo.keywords} image={seo.og_image || heroImg} jsonLd={[orgLd(social, footer, branding), websiteLd()]} />

      <section className="relative grain overflow-hidden bg-black min-h-[560px] sm:min-h-[680px] lg:min-h-[820px]" data-testid="home-hero">
        <img
          src={cldOpt(heroImg, 1920)}
          alt="VVS Lab Jewelry hero"
          fetchPriority="high"
          decoding="async"
          onError={(e) => { if (!e.currentTarget.dataset.fbk) { e.currentTarget.dataset.fbk = "1"; e.currentTarget.src = FALLBACK_HERO_IMG; } }}
          className="absolute inset-0 h-full w-full object-cover opacity-80"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/50 to-black/80" />
        <div className="relative mx-auto flex min-h-[560px] max-w-7xl flex-col justify-end px-4 pb-16 pt-32 sm:min-h-[680px] sm:px-8 sm:pb-20 sm:pt-56 lg:min-h-[820px] lg:pb-28">
          {h.eyebrow && (
            <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="text-xs font-bold uppercase tracking-[0.4em] text-white/80" data-testid="hero-eyebrow">
              {h.eyebrow}
            </motion.p>
          )}
          <motion.h1 initial={false} className="mt-6 max-w-3xl font-display text-4xl font-black leading-[1.05] tracking-tighter text-white sm:text-5xl lg:text-6xl" data-testid="hero-title">
            {h.title_line1 && <>{h.title_line1}<br /></>}
            {h.title_line2 && <><span className="text-outline">{h.title_line2}</span><br /></>}
            {h.title_line3 && <>{h.title_line3}</>}
          </motion.h1>
          {h.subtitle && (
            <motion.p initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.3 }} className="mt-6 max-w-md text-base leading-relaxed text-zinc-300 sm:text-lg" data-testid="hero-subtitle">
              {h.subtitle}
            </motion.p>
          )}
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.45 }} className="mt-10 flex flex-wrap gap-4">
            {h.cta1_label && h.cta1_link && (
              <Link to={h.cta1_link} data-testid="hero-cta1-btn" className="border-2 border-white bg-white px-8 py-4 text-xs font-bold uppercase tracking-[0.2em] text-black transition-colors duration-200 hover:bg-transparent hover:text-white">
                {h.cta1_label}
              </Link>
            )}
            {h.cta2_label && h.cta2_link && (
              <Link to={h.cta2_link} data-testid="hero-cta2-btn" className="border-2 border-white px-8 py-4 text-xs font-bold uppercase tracking-[0.2em] text-white transition-colors duration-200 hover:bg-white hover:text-black">
                {h.cta2_label}
              </Link>
            )}
          </motion.div>
        </div>
      </section>

      <div className="overflow-hidden border-y-2 border-black bg-black py-3">
        <div className="marquee whitespace-nowrap text-[11px] font-bold uppercase tracking-[0.35em] text-white">
          {Array(8).fill("Passes the diamond tester ★ Free worldwide shipping ★ Lifetime shine guarantee ★ ").join("")}
        </div>
      </div>

      <DealSection deal={deal} />

      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-8 lg:py-28">
        <p className="text-xs font-bold uppercase tracking-[0.4em] text-zinc-500">Collections</p>
        <h2 className="mt-3 font-display text-lg font-black tracking-tight text-black md:text-lg">Pick Your Poison</h2>
        <div className="mt-10 grid grid-cols-1 gap-4 md:grid-cols-12">
          {categories.filter((c) => !c.parent_slug && c.image).map((cat, i) => (
            <Link
              key={cat.slug}
              to={`/${cat.slug}`}
              data-testid={`category-tile-${cat.slug}`}
              className={`group relative overflow-hidden border-2 border-black ${i === 0 ? "md:col-span-6 md:row-span-2 min-h-[380px]" : "md:col-span-3 min-h-[220px]"}`}
            >
              <img src={cldOpt(cat.image, 800)} alt={cat.name} loading="lazy" className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/30 to-transparent" />
              <div className="absolute bottom-0 p-6">
                <h3 className="font-display text-base font-bold tracking-tight text-white">{cat.name}</h3>
                <p className="mt-1 flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.2em] text-white opacity-0 transition-opacity duration-300 group-hover:opacity-100">
                  Shop now <ArrowRight size={12} />
                </p>
              </div>
            </Link>
          ))}
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 pb-4 sm:px-8">
      </section>

      <FeaturedCollections />

      <WholesaleCards />

      <FAQSection />

      <section className="border-t-2 border-black bg-black">
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-8">
          <p className="text-xs font-bold uppercase tracking-[0.4em] text-zinc-500">The Ice Report</p>
          <h2 className="mt-3 font-display text-lg font-black tracking-tight text-white">Know Before You Glow</h2>
          <div className="mt-10 grid gap-6 md:grid-cols-3">
            {posts.map((post) => (
              <Link key={post.slug} to={`/blog/${post.slug}`} data-testid={`home-blog-card-${post.slug}`} className="group border-2 border-white bg-black transition-transform duration-300 hover:-translate-y-1 hover:shadow-[6px_6px_0_#fff]">
                <div className="aspect-[16/9] overflow-hidden border-b-2 border-white">
                  <img src={cldOpt(post.cover_image, 800)} alt={post.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                </div>
                <div className="p-6">
                  <h3 className="font-display text-sm font-bold text-white">{post.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-zinc-400">{post.excerpt}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
